import { Box } from '@mui/material';
import PlayIcon from '../../assets/images/PlayIcon.png';
import Checkmark from '../../assets/images/Checkmark.png';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from '../../Actions/app';
import './PlayContainer.css';
import { useNavigate } from 'react-router-dom';
import { ReactNode, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Replay } from '@mui/icons-material';
import * as Immutable from 'immutable';
import { LevelData } from 'Types';
import { RootState } from 'Reducers';
import { CompletedChapters, resolvePlayRoute } from 'Utils/tutorialCompletion';
import {
  PreviewPhraseSize,
  ROADMAP_PLAY_COLUMN_WIDTH,
  ROADMAP_TEXT_COLUMN_PADDING,
  roadmapPictureMinPhraseWidth,
  useRoadmapPreviewFits,
} from './roadmapLayout';
import { PreviewPhrase } from './PreviewPhrase/PreviewPhrase';
import { previewPhrases } from './PreviewPhrase/previewPhrases';
import { previewPhrasesMinimumWidth } from './PreviewPhrase/previewPhraseFits';
import { isSpaceForThePage } from './roadmapKeys';
import { ORANGE_TEXT_ON_DARK } from 'Constants/AccessibleColors';

// The engraved phrase is drawn this high in the Play bar, at every width.
const PREVIEW_PHRASE_HEIGHT = 150;

interface SelectedLevelProgress {
  highest_tier_complete?: number;
}

interface PlayContainerProps {
  selectedLevel: LevelData;
  levelSelect: number;
  level_number: number;
  name: string;
  description: string;
  selectedLevelProgress: SelectedLevelProgress | null | undefined;
  tierCountForLevel: number;
}

export const PlayContainer = (props: PlayContainerProps) => {
  const {
    selectedLevel, //object
    levelSelect,
    level_number,
    name,
    description,
    selectedLevelProgress,
    tierCountForLevel,
  } = props;
  let highestTierComplete = 0;
  if (selectedLevelProgress) {
    highestTierComplete = selectedLevelProgress.highest_tier_complete;
  }

  const barRef = useRef<HTMLDivElement | null>(null);
  const phraseSrc = `https://${selectedLevel.srcs}`;
  // A level with notation draws it live, re-spaced to the box; one without falls back to
  // its picture. Each brings the narrowest width at which it still reads.
  const engraved = !!previewPhrases[level_number];
  const { minPhraseWidth: pictureMinWidth, onPictureLoad } = usePicturePhraseMinWidth();
  const minPhraseWidth = engraved
    ? previewPhrasesMinimumWidth(PREVIEW_PHRASE_HEIGHT)
    : pictureMinWidth;
  const showsPreview = useRoadmapPreviewFits(barRef, minPhraseWidth);

  return (
    <Box ref={barRef} className={`container-styles-1`}>
      <Box className="container-styles-2">
        <Box sx={columnSx}>
          <Box className="container-styles-3">
            <Box className="play-container-text-container">
              <p className="play-container-text-description">
                LEVEL {level_number}: {name}
              </p>
            </Box>
            <p className="play-container-text">{description}</p>
          </Box>
          <Completion
            highestTierComplete={highestTierComplete}
            tierCountForLevel={tierCountForLevel}
          />
        </Box>
        {level_number != 0 && (
          <RoadmapPreviewBox visible={showsPreview} fillWidth={engraved}>
            {engraved ? (
              <PreviewPhrase
                levelNumber={level_number}
                levelTitle={`Level ${level_number}: ${name}`}
                fallbackSrc={phraseSrc}
                height={PREVIEW_PHRASE_HEIGHT}
              />
            ) : (
              <img
                style={{ display: 'block', maxWidth: '100%', maxHeight: '150px' }}
                src={phraseSrc}
                onLoad={onPictureLoad}
              />
            )}
          </RoadmapPreviewBox>
        )}
        <Box
          sx={{
            flex: `0 0 ${ROADMAP_PLAY_COLUMN_WIDTH}px`,
            margin: '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Play
            levelSelect={levelSelect}
            level_number={level_number}
            highestTierComplete={highestTierComplete}
          />
          <ReplayTutorialButton
            level_number={level_number}
            highestTierComplete={highestTierComplete}
          />
        </Box>
      </Box>
    </Box>
  );
};

// The text column and the preview box share what the Play column leaves, half each, and
// may shrink below their content's width, so the bar never grows past the window.
// (The flex basis is the content box, so the text column's padding comes on top.)
const columnSx = {
  flex: '1 1 0',
  minWidth: 0,
  height: '100%',
  padding: `${ROADMAP_TEXT_COLUMN_PADDING}px`,
} as const;

// The picture of the phrase: its own pixel size, read when it loads, gives the narrowest
// width at which it is still 72px high. The last size read is kept until the next level's
// picture loads, so the box does not pop in and out on a level change.
const usePicturePhraseMinWidth = () => {
  const [pictureSize, setPictureSize] = useState<PreviewPhraseSize | null>(null);
  const onPictureLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setPictureSize({ width: naturalWidth, height: naturalHeight });
    }
  }, []);
  const minPhraseWidth = pictureSize ? roadmapPictureMinPhraseWidth(pictureSize) : null;
  return { minPhraseWidth, onPictureLoad };
};

interface RoadmapPreviewBoxProps {
  visible: boolean;
  // The engraved phrase fills the box's width (it re-spaces to it); a picture keeps its
  // own size and is centred.
  fillWidth: boolean;
  children: ReactNode;
}

// The preview box: the PREVIEW label over the phrase. When hidden it stays mounted (so the
// phrase still loads and its size is known) but takes no room.
const RoadmapPreviewBox = ({ visible, fillWidth, children }: RoadmapPreviewBoxProps) => (
  <Box
    data-testid="roadmap-preview-box"
    hidden={!visible}
    sx={{
      flex: '1 1 0',
      minWidth: 0,
      display: visible ? undefined : 'none',
      minHeight: '200px',
      textAlign: 'left',
      // backgroundColor: '#B9BEC4' // the new color once we update the png
      backgroundColor: 'rgba(226,226,226)',
      margin: '0px',
    }}
  >
    {/*
      The label and the phrase are one unit, centred in the box: once the
      phrase stops growing (it is capped by height), the box keeps
      widening and the unit stays in the middle, the label keeping its
      place over the phrase's left side.
    */}
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '0 5px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: fillWidth ? '100%' : undefined,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box sx={{ marginLeft: '23px', marginTop: '20px' }} className={'preview-text'}>
          PREVIEW
        </Box>
        <Box
          data-testid="roadmap-preview-phrase-room"
          sx={{ padding: '3px 0 1px 0', width: fillWidth ? '100%' : undefined, maxWidth: '100%' }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  </Box>
);

interface ReplayTutorialButtonProps {
  level_number: number;
  highestTierComplete: number;
}

export const ReplayTutorialButton = ({
  level_number,
  highestTierComplete,
}: ReplayTutorialButtonProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onReplayTutorial = useCallback(() => {
    dispatch(appActions.fetchTutorialData());
    navigate(`/tutorial`);
  }, [dispatch, navigate]);
  // Don't show button if level is 0 or no tiers are complete
  if (level_number == 0 || highestTierComplete == 0) {
    return null;
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={onReplayTutorial}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: '5px',
        marginTop: '10px',
        minHeight: '44px',
        border: 0,
        background: 'transparent',
        appearance: 'none',
        WebkitAppearance: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        cursor: 'pointer',
        // Not dimmed: brand orange at 75% brightness read at 2.6:1 on the panel (WCAG AA asks 4.5:1).
        color: ORANGE_TEXT_ON_DARK,
      }}
    >
      <Replay sx={{ width: '18px', height: '18px' }} />
      <span style={{ fontSize: '14px', fontWeight: '700', textDecoration: 'underline' }}>
        Replay Tutorial
      </span>
    </Box>
  );
};

interface PlayProps {
  levelSelect: number;
  level_number: number;
  highestTierComplete: number;
}

const Play = ({ levelSelect: _levelSelect, level_number, highestTierComplete }: PlayProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Completed tutorial chapters for this level (hydrated at startup). Lets a
  // finished-tutorial user skip to gameplay even before their first tier
  // completes - see resolvePlayRoute / MUS-393.
  const completedChapters = useSelector(
    (state: RootState) =>
      Immutable.get(state.tutorialReducer?.completed, level_number) as CompletedChapters,
  );

  const onPlay = useCallback(() => {
    // Will grab tutorials and redirect in the sagas
    dispatch(appActions.fetchTutorialData());
    navigate(resolvePlayRoute(level_number, highestTierComplete, completedChapters));
  }, [dispatch, navigate, highestTierComplete, level_number, completedChapters]);

  useEffect(() => {
    // A focused button, link or field keeps Space for its own action.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isSpaceForThePage(event)) {
        onPlay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onPlay]);
  return (
    <Box>
      <Box className={'play-text-styles'}>PLAY</Box>
      <Box
        component="button"
        type="button"
        aria-label="Play selected roadmap level"
        onClick={() => {
          onPlay();
        }}
        className="play-container-styles"
      >
        <img src={PlayIcon} />
      </Box>
    </Box>
  );
};

// The tier segments: a circle (or the check on the highest tier complete) for each tier,
// joined by lines. Each circle keeps 12px on either side (10px around the check) where
// the row has room; in a narrow column that room gives way first, down to 2px, so the row
// stays inside its column. The marks and the lines are all children of the one row, and
// every line takes an equal share of what the marks leave, so the lines stay the same
// length as the row widens or narrows, whichever mark (circle or check) ends each one.
export const Completion = (props: { highestTierComplete: number; tierCountForLevel: number }) => {
  const { highestTierComplete, tierCountForLevel } = props;
  const completeColor = '#6F87F1';
  const incompleteColor = '#767C8C';
  const colorFor = (tier: number) =>
    highestTierComplete >= tier ? completeColor : incompleteColor;
  return (
    <Box
      data-testid="tier-progress"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        height: '40px',
        marginTop: '6px',
      }}
    >
      {/* The first circle: grey with a little more room after it while no tier is complete. */}
      <Box
        sx={
          highestTierComplete == 0 ? { ...circleRoomStyles, marginRight: '10px' } : circleRoomStyles
        }
      >
        <Box sx={{ ...circleStyles, backgroundColor: colorFor(1) }} />
      </Box>
      {[...Array(tierCountForLevel)].flatMap((_, v) => [
        <Box key={`line-${v}`} sx={{ ...lineStyles, backgroundColor: colorFor(v + 1) }} />,
        // The check marks the highest tier complete; every other tier ends in a circle.
        highestTierComplete == v + 1 ? (
          <Box key={`mark-${v}`} sx={checkRoomStyles}>
            <img src={Checkmark} />
          </Box>
        ) : (
          <Box key={`mark-${v}`} sx={circleRoomStyles}>
            <Box sx={{ ...circleStyles, backgroundColor: colorFor(v + 1) }} />
          </Box>
        ),
      ])}
    </Box>
  );
};

// A circle (14px) or the check (35px) and the room around it: the full room where the row
// has space, down to 2px a side where it does not.
const roomAround = (markWidth: number, side: number) => ({
  flex: `0 1 ${markWidth + 2 * side}px`,
  minWidth: `${markWidth + 4}px`,
});
const circleRoomStyles = { ...roomAround(14, 12), display: 'flex', justifyContent: 'center' };
const checkRoomStyles = { ...roomAround(35, 10), textAlign: 'center' } as const;

const lineStyles = {
  height: '5px',
  borderRadius: '99px',
  // Every line grows from nothing by the same share, so all of them are one length.
  flex: '1 1 0',
  minWidth: '4px',
};

const circleStyles = {
  width: '14px',
  height: '14px',
  borderRadius: '50%',
};
